// PersonaConversationSurface — shared chat-surface shell extracted in v0.4
// Phase 1 keystone. Mounted by both TrailheadCore and PersonaChat orchestrator
// pages. Receives loadingStatus / phaseEvents / wsLastEventMs / inFlightTurnId
// / errorTurnId / viewportTurnId via props (orchestrator owns ScrollObserver +
// LiveProgressPipe + ConversationProvider above this surface).

import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { TrailheadLayout } from '../trailhead/TrailheadLayout';
import { ConversationInterface } from './ConversationInterface';
import { useConversation } from '../../contexts/ConversationContext';
import { useReloadOnPeerEvent } from '../../hooks/useReloadOnPeerEvent';
import { useGreetingPending } from '../../hooks/useGreetingPending';
import { useCrewArriving } from '../../hooks/useCrewArriving';
import { useHillTargets } from '../../hooks/useHillTargets';
import type { HillTargetSet, HillForkRequest } from '../../types/hill';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { FiArrowLeft, FiMessageSquare } from 'react-icons/fi';
import { LlmDataWarning } from '../common/LlmDataWarning';
import { useTheaterMode, THEATER_PREF_KEY } from '../../contexts/TheaterModeContext';
import { CouncilContext } from '../../contexts/CouncilContext';
import { useLensSnapshot } from '../../hooks/useLensSnapshot';
import { useMessagesPeerMetadata } from '../../hooks/useMessagesPeerMetadata';
import { useWakeBrief } from '../../hooks/useWakeBrief';
import { WakeBriefBanner } from './WakeBriefBanner';
import PersonaBadge from '../common/PersonaBadge';
import { PersonaHoverCard } from '../personas/PersonaHoverCard';
import { SkillBadge } from '../skills/SkillBadge';
import ChatDagCanvas from './chatdag/ChatDagCanvas';
import { ChatDagErrorBoundary } from './chatdag/ChatDagErrorBoundary';
import { ExpeditionLog } from '../trailhead/ExpeditionLog';
import { useExpeditionLog } from '../../stores/expeditionLog';
import { councilWsClient } from '../../services/councilWsClient';
import { InvitePeerHost } from './InvitePeerHost';
import { FoldBackDialog } from './FoldBackDialog';
import {
  PersonaChatUIProvider,
  usePanelHandlerRefs,
} from '../../contexts/PersonaChatUIContext';
import { ChatDagRuntimeProvider, EMPTY_COLLAPSED } from '../../contexts/ChatDagRuntimeContext';
import type { GroupingDim } from './chatdag/types';
import { ChatDagDimensionsProvider } from '../../contexts/ChatDagDimensionsContext';
import type { PhaseEvent } from '../../hooks/usePersonaToolProgress';
import { fanReducer } from './internals/fanReducer';
import { artifactExpandReducer } from './internals/artifactExpandReducer';
import { SessionCostChip } from './internals/SessionCostChip';
import { WorkshopRow } from '../trailhead/workshop/WorkshopRow';
import { TrailObjectiveBanner } from '../trailhead/TrailObjectiveBanner';
import type { PersonaChatAdapter } from '../../adapters/personaChatAdapter';
import { useDiagMount, useDiagWatch } from '../../utils/trailhead_diag';

/**
 * Council Trail Task 6.8 — surface-root WakeBrief slot.
 *
 * Calls useWakeBrief unconditionally (the hook is safe to call without
 * a CouncilProvider — it just hits /api/sessions/{id}/council/wake-brief
 * directly). Renders the banner if a brief is due AND the surface is
 * inside a CouncilProvider; otherwise stays absent (Solo Trail surfaces
 * never see it).
 *
 * The two-gate logic — useWakeBrief returns brief AND CouncilContext is
 * not null — keeps Solo Trail surfaces clean even if /wake-brief
 * accidentally returns a payload (the council surfaces are the operator
 * mental model where the banner makes sense).
 */
const CouncilWakeBriefBannerSlot: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const ctx = useContext(CouncilContext);
  const { brief, dismiss } = useWakeBrief(sessionId);
  if (!ctx) return null;
  if (!brief) return null;
  return (
    <WakeBriefBanner
      brief={brief}
      onApproveAll={() => { /* v0.1 — bulk-approve pending proposals */ }}
      onDismiss={dismiss}
      onReviewActivity={() => { /* v0.1 — open activity drawer */ }}
    />
  );
};

// Inner message-stream component (was the small PersonaConversationSurface
// in pre-v0.4 inline form; renamed to free up the export name for the
// expanded shell below).
//
// Council Trail v0.1 Item 1 — bridges useMessagesPeerMetadata + LensContext
// into the ConversationInterface → MessageList prop chain. Both reads are
// safe outside a CouncilProvider / LensProvider (hook drops events when
// council state is null; LensContext read is optional with null fallback)
// per the additive-integration discipline (see ship-journal watchlist #4).
const ConversationMessageStream: React.FC<{
  persona: any;
  sessionId: string;
  isFallback: boolean;
  loadingStatus: string;
  phaseEvents: PhaseEvent[];
  autoFocus?: boolean;
}> = ({ persona, sessionId, isFallback, loadingStatus, phaseEvents, autoFocus }) => {
  const peerMetadataByMessageId = useMessagesPeerMetadata(sessionId);
  const lensSnapshot = useLensSnapshot();
  return (
    <ConversationInterface
      title={persona.name}
      subtitle={
        isFallback
          ? persona.shortDescription || 'AI Assistant'
          : persona.primarySpecialization || 'AI Assistant'
      }
      avatarName={persona.name}
      avatarSpecialization={isFallback ? undefined : persona.primarySpecialization}
      avatarUrl={isFallback ? undefined : persona.avatarUrl}
      sessionId={sessionId}
      personaId={persona.id}
      personaName={persona.name}
      compactHeader={!isFallback}
      loadingStatus={loadingStatus}
      phaseEvents={phaseEvents}
      autoFocus={autoFocus}
      peerMetadataByMessageId={peerMetadataByMessageId}
      lensSnapshot={lensSnapshot}
    />
  );
};

export interface PersonaConversationSurfaceProps {
  persona: any;
  session: any | undefined;
  sessionId: string;
  adapter: PersonaChatAdapter;
  isFallback?: boolean;
  sessionSkills?: any[];
  onShowSkillPicker?: () => void;
  onBack: () => void;
  // Live-progress lifted from orchestrator (fed by <LiveProgressPipe>).
  loadingStatus: string;
  phaseEvents: PhaseEvent[];
  wsLastEventMs: number | null;
  inFlightTurnId: string | null;
  errorTurnId: string | null;
  // Viewport state lifted from orchestrator (fed by <ScrollObserver>).
  viewportTurnId: string | null;
  setViewportTurnId: (id: string | null) => void;
  // Trailhead orchestrator passes true so the picker → chat-mount path
  // lands the cursor in the textarea; legacy PersonaChat omits this.
  autoFocus?: boolean;
  // Initial state for the ChatMap panel when localStorage has no
  // prior preference. Trailhead passes true so a fresh trail opens
  // with the map visible (operator runs chat+map in tight correlation
  // for the multi-persona/artifact-groupings flow). PersonaChat omits
  // for the boring-and-proven closed-by-default.
  defaultPanelOpen?: boolean;
  // localStorage namespace for panel open/width persistence. Per-surface
  // keys prevent cross-contamination (Trailhead's default-open writing '1'
  // would otherwise also flip PersonaChat to open on its next load).
  // Defaults to the legacy 'personaChat*' keys for back-compat.
  panelStorageKey?: string;
  // TH-1 — convene set out a greeting; suppress both empty-states until the
  // sherpa's first turn lands (TURN_ADDED) or a terminal/timeout clears it.
  greetingPending?: boolean;
}

export const PersonaConversationSurface: React.FC<PersonaConversationSurfaceProps> = ({
  persona,
  session,
  sessionId,
  adapter: _adapter,
  isFallback = false,
  sessionSkills = [],
  onShowSkillPicker,
  onBack,
  loadingStatus,
  phaseEvents,
  wsLastEventMs,
  inFlightTurnId,
  errorTurnId,
  viewportTurnId,
  setViewportTurnId: _setViewportTurnId,
  autoFocus = false,
  defaultPanelOpen = false,
  panelStorageKey = 'personaChat',
  greetingPending = false,
}) => {
  const panelOpenKey = `${panelStorageKey}PanelOpen`;
  const panelWidthKey = `${panelStorageKey}PanelWidth`;
  const { darkMode } = useTheme();
  const { state: conversationState, reloadMessages, sendFork } = useConversation();
  // C2.1 sp5 — repaint when a peer turn lands (turn_added) or after a reconnect
  // (ws_connected): a full server-truth re-pull, not optimistic patching.
  useReloadOnPeerEvent(sessionId, reloadMessages);
  // G3a — Expedition Log: backfill on mount, merge live frames, catch up on
  // reconnect (ws_connected). live ≡ reload (G4b doctrine).
  const { open: logOpen, init: logInit, onFrame: logOnFrame, onReconnect: logOnReconnect } = useExpeditionLog();
  React.useEffect(() => {
    if (!sessionId) return;
    logInit(sessionId);
    const unsub = councilWsClient.subscribe(sessionId, (frame) => {
      if (frame.event_type === 'event_log_entry') logOnFrame(frame);
      else if ((frame as any).event_type === 'ws_connected') logOnReconnect(sessionId);
    });
    return unsub;
  }, [sessionId, logInit, logOnFrame, logOnReconnect]);
  // SP3b — bind hill-target setters for the in-node SpineHillEditPopover (via
  // PersonaChatUIContext). A fork mutates the blackboard → reloadMessages repaints
  // the new spine; an in-place change just refreshes the hill-targets cache.
  const { setTarget: setHillTarget, forkTarget: forkHillTargetMut } = useHillTargets(sessionId);
  const handleEditHill = useCallback(async (payload: HillTargetSet) => { await setHillTarget(payload); }, [setHillTarget]);
  const handleForkHill = useCallback(async (body: HillForkRequest) => { await forkHillTargetMut(body); reloadMessages(); }, [forkHillTargetMut, reloadMessages]);
  // §3 (F8) — camp-root re-roll seam: a camp-root fork uses the camp marker as M
  // (fromMessageId == spineRootMessageId). sendFork fans the pending; the answer
  // arrives via the TURN_ADDED reload (no explicit reloadMessages needed).
  const handleForkReroll = useCallback(
    (spineRootMessageId: string, _personaId: string, personaName: string) =>
      sendFork('', { spineId: spineRootMessageId, fromMessageId: spineRootMessageId, mode: 'reroll', residentName: personaName }),
    [sendFork],
  );
  // TH-1 (R1) — greetingPending is load-bearing for fail-safety (see useGreetingPending):
  // clears on the first terminal event OR a 15s timeout so a silent pre-dispatch failure
  // can never leave both empty-states suppressed forever.
  const greetPending = useGreetingPending(sessionId, greetingPending);
  // TH-1 — "the crew is arriving…" chrome: shows on CREW_GREETED, clears on the
  // first terminal event (turn_added OR peer_turn_failed — R4).
  const crewArriving = useCrewArriving(sessionId);
  // TH-3a — theater is a property of the trail surface, not the whole app: restore
  // the persisted preference on mount, and always clear the live flag on unmount so
  // the app chrome returns when leaving the trail.
  const { theater, setTheater } = useTheaterMode();
  useEffect(() => {
    try { setTheater(localStorage.getItem(THEATER_PREF_KEY) === '1'); } catch { /* default off */ }
    return () => setTheater(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const showZeroStateHint = conversationState.messages.length === 0 && !greetPending;
  const [showPersonaDetails, setShowPersonaDetails] = useState(false);
  const personaDetailsRef = useRef<HTMLDivElement>(null);

  useDiagMount('surface', 'PersonaConversationSurface', {
    personaId: persona?.id,
    personaName: persona?.name,
    sessionId,
    isFallback,
  });
  useDiagWatch('surface', 'conversation state', {
    messages: conversationState.messages.length,
    status: conversationState.status,
    showZeroStateHint,
    showPersonaDetails,
  });
  useDiagWatch('surface', 'live progress', {
    loadingStatus: loadingStatus || null,
    phaseEvents: phaseEvents?.length ?? 0,
    wsLastEventMs,
    inFlightTurnId,
    errorTurnId,
    viewportTurnId,
  });
  useDiagWatch('surface', 'session skills', { count: sessionSkills?.length ?? 0 });
  // Task 29 — Panel handler refs wire in-bubble click-throughs
  // (CreateArtifactsResult) to the Panel's imperative focus/scroll
  // without prop-drilling through MessageList → MessageBubble →
  // ToolCallPill → CreateArtifactsResult.
  const panelHandlerRefs = usePanelHandlerRefs();

  // v0.3 Chat DAG: Set of parent tool_call_ids whose peer fan is
  // currently user-expanded. The FanCollapsedChip toggles membership
  // via toggleFanExpansion on PersonaChatUIContext; the processor
  // reads via ChatDagRuntimeContext.collapsedFanIds.
  const [collapsedFanIds, dispatchFan] = React.useReducer(fanReducer, EMPTY_COLLAPSED);
  const toggleFanExpansion = React.useCallback(
    (id: string) => dispatchFan({ type: 'toggle', id }),
    [],
  );

  // v0.3 controls-panel globals — surfaced via the DAG's ControlsPanel.
  const [allFansCollapsed, setAllFansCollapsed] = React.useState<boolean>(false);
  const [showPhaseStacks, setShowPhaseStacks] = React.useState<boolean>(true);
  // Per-turn phase-stack collapse state — fanReducer is reused since
  // the semantics are identical (toggle-membership-of-a-string-set).
  const [collapsedPhaseStackIds, dispatchPhaseStack] = React.useReducer(
    fanReducer,
    EMPTY_COLLAPSED,
  );
  const togglePhaseStackCollapse = React.useCallback(
    (turnKey: string) => dispatchPhaseStack({ type: 'toggle', id: turnKey }),
    [],
  );
  const [allPhaseStacksCollapsed, setAllPhaseStacksCollapsed] =
    React.useState<boolean>(false);

  // Trailmap L2 Drip 1 — per-turn artifact disclosure. Reuses fanReducer
  // (toggle-membership-of-a-string-set), keyed by source_turn_id. Default
  // empty = all collapsed (progressive disclosure).
  const [expandedArtifactTurnIds, dispatchArtifacts] = React.useReducer(
    fanReducer,
    EMPTY_COLLAPSED,
  );
  const toggleArtifactsExpanded = React.useCallback(
    (turnKey: string) => dispatchArtifacts({ type: 'toggle', id: turnKey }),
    [],
  );

  // Trailmap L2 Drip 3 — per-artifact expansion set (which tiles are blown
  // open to content). Dedicated reducer (fanReducer is toggle-only; this
  // adds collapseAll for the flat-pill / ESC / ⋯ menu). Declared before the
  // Drip-2 setActiveDim callback below, which dispatches collapseAll on flat.
  const [expandedArtifactIds, dispatchArtifactExpand] = React.useReducer(
    artifactExpandReducer,
    EMPTY_COLLAPSED,
  );
  const toggleArtifactExpansion = React.useCallback(
    (artifactId: string) => dispatchArtifactExpand({ type: 'toggle', id: artifactId }),
    [],
  );
  const collapseAllArtifacts = React.useCallback(
    () => dispatchArtifactExpand({ type: 'collapseAll' }),
    [],
  );

  // Source Spines SP-2 — per-blueprint-card spine disclosure. useState (not the
  // artifact reducer) because the setter is functional: it must survive a
  // toggle-vs-stream commit race (a TURN_ADDED reload mid-toggle). A seedling
  // card defaults EXPANDED so its "shape the intent below" CTA isn't orphaned;
  // reseed on session switch so stale ids never leak across trails.
  const [expandedCardSpineIds, setExpandedCardSpineIds] = React.useState<
    ReadonlySet<string>
  >(() => new Set());
  const toggleCardSpineExpansion = React.useCallback((cardId: string) => {
    setExpandedCardSpineIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  }, []);
  // SP-3a — idempotent set-ADD (never delete). Both the seedling self-seed and
  // the operator-touched F5 default-expand route here so two ref-guarded seeders
  // in one commit can't flip-flop a toggle to net-collapsed. Stable identity if
  // the id is already present (no needless re-render).
  const seedCardSpineExpanded = React.useCallback((cardId: string) => {
    setExpandedCardSpineIds((prev) => (prev.has(cardId) ? prev : new Set(prev).add(cardId)));
  }, []);
  // NB: seeding lives IN BlueprintNode (a seedling card self-expands once its
  // async manifest resolves) — NOT a messages-based reseed here. The seedling
  // face is manifest state, not chat messages, and a [sessionId] effect fires
  // with empty messages on cold load (plan-triad fold). Stale card ids across a
  // session switch are inert (card ids are manifest-unique).

  // Trailmap L2 Drip 2 — active grouping dimension, persisted per-surface.
  // LAZY init (synchronous) so the first layout runs at the saved dim — no
  // flat→grouped flash on mount (spec §3.6). Key is a sibling of
  // panelOpenKey/panelWidthKey (:169-170); per-surface, not session-scoped.
  const dimKey = `${panelStorageKey}Dim`;
  const [activeDim, setActiveDimState] = React.useState<GroupingDim>(() => {
    try {
      const v = localStorage.getItem(dimKey);
      // Flat ('off') removed in the 2026-06-14 dogfood recon — a persisted 'off'
      // migrates to 'turn' (the new default) by falling through.
      if (v === 'turn' || v === 'phase' || v === 'persona') return v;
    } catch { /* private-mode etc */ }
    return 'turn';
  });
  const setActiveDim = React.useCallback((dim: GroupingDim) => {
    // Drip 3 DC-12 — flat is browse-collapsed: leaving a grouped dim drops
    // all open tiles (expansion only lives in a grouped dim). dispatch is a
    // stable useReducer ref → no deps change.
    if (dim === 'off') dispatchArtifactExpand({ type: 'collapseAll' });
    setActiveDimState(dim);
    try { localStorage.setItem(dimKey, dim); } catch { /* ignore */ }
  }, [dimKey]);

  // v0.4 Task 1: focal-state ownership. tabFocusTurnId set by
  // keyboard-focus on a DAG node. lastInFlightTurnId captures the most
  // recent turn that was in-flight so the focal chain keeps that turn
  // "hot" for a moment after the assistant response lands (cleared on
  // scroll). inFlightStartedAtMs gates the first-2s RICH_DEFAULT
  // authoritative window in layoutChatDag.
  const [tabFocusTurnId, setTabFocusTurnId] = React.useState<string | null>(
    null,
  );
  const [lastInFlightTurnId, setLastInFlightTurnId] = React.useState<
    string | null
  >(null);
  const [inFlightStartedAtMs, setInFlightStartedAtMs] = React.useState<
    number | null
  >(null);
  const prevInFlightRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const prev = prevInFlightRef.current;
    if (prev != null && inFlightTurnId == null) {
      // Turn ended — keep it as "lastInFlight" so the focal chain
      // doesn't immediately snap away before the answer is read.
      setLastInFlightTurnId(prev);
      setInFlightStartedAtMs(null);
    } else if (prev == null && inFlightTurnId != null) {
      // Turn started — stamp the start time for the first-2s window.
      setInFlightStartedAtMs(Date.now());
    }
    prevInFlightRef.current = inFlightTurnId;
  }, [inFlightTurnId]);

  // Trailmap L2 Drip 3 — drop the artifact-expansion set on conversation
  // switch so a stale open tile doesn't bleed across sessions (DC-13).
  // Drip-1 turn-disclosure is left alone — it self-corrects on reload.
  React.useEffect(() => {
    dispatchArtifactExpand({ type: 'collapseAll' });
  }, [sessionId]);

  // v0.4 Task 11: scroll clears lastInFlightTurnId so the focal
  // chain doesn't hold the just-completed turn "hot" when the user
  // scrolls away. Capture=true so any scrollable ancestor fires.
  React.useEffect(() => {
    const handler = () => setLastInFlightTurnId(null);
    window.addEventListener('scroll', handler, {
      passive: true,
      capture: true,
    });
    return () => window.removeEventListener('scroll', handler, true);
  }, []);

  // Task 13: WS-health watchdog. When loading + no phase events for
  // 120s, toast once per open turn. Resets when status leaves
  // 'loading'. Distinct from the backend's turn-lifecycle watchdog
  // (which owns its own time budget); this watchdog tells the USER
  // that the DAG may be stale.
  const { showToast } = useToast();
  const [healthToastShown, setHealthToastShown] = React.useState(false);
  React.useEffect(() => {
    if (inFlightTurnId == null) {
      setHealthToastShown(false);
    }
  }, [inFlightTurnId]);
  React.useEffect(() => {
    if (inFlightTurnId == null) return;
    const t = window.setInterval(() => {
      if (wsLastEventMs == null) return;
      if (Date.now() - wsLastEventMs > 120_000 && !healthToastShown) {
        showToast(
          'Connection may be degraded — DAG state may be stale.',
          'error',
        );
        setHealthToastShown(true);
      }
    }, 5000);
    return () => window.clearInterval(t);
  }, [inFlightTurnId, wsLastEventMs, healthToastShown, showToast]);

  // v0.3 spine click: locate the bubble by data-message-id and
  // scroll its WRAPPER (which includes any PersistedPhaseTrail
  // sitting above the bubble) to the top of the scroll viewport.
  // This matches the turn-end scroll methodology in MessageList —
  // phase trail collapsed at top, then the message content below —
  // so a DAG-driven jump and a turn-end-driven scroll leave the user
  // in the same visual frame. Returns false if the bubble isn't in
  // the DOM (virtualization / not-loaded history).
  const focusBubble = React.useCallback(
    (messageId: string): boolean => {
      const el = document.querySelector(`[data-message-id="${messageId}"]`);
      if (!el) {
        showToast(
          'Message not in view — scroll the history or wait for load.',
          'info',
        );
        return false;
      }
      const target = (el.parentElement as HTMLElement | null) ?? el;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('chatdag-bubble-flash');
      window.setTimeout(() => {
        el.classList.remove('chatdag-bubble-flash');
      }, 1400);
      return true;
    },
    [showToast],
  );

  // C2.1 sp4 — operator invite affordance. Enabled only on the Trailhead/council
  // surface (panelStorageKey); the chat bubble + the ChatMap turn node both open
  // this one dialog via PersonaChatUIProvider.openInvitePeer.
  const inviteEnabled = panelStorageKey === 'trailhead';
  const [inviteFromTurnId, setInviteFromTurnId] = useState<string | null>(null);
  // Shared spines P2 — camp-menu trail-join; the dialog omits from_turn_id
  // (backend parents the joiner's camp at the trail tip at materialization).
  const [inviteTrailId, setInviteTrailId] = useState<string | null>(null);

  // C2.2 — operator fold-back affordance (same gating as invite: Trailhead surface).
  // The ChatMap Camp chip opens this one dialog via PersonaChatUIProvider.openFoldBack.
  const foldBackEnabled = panelStorageKey === 'trailhead';
  const [foldBackCampId, setFoldBackCampId] = useState<string | null>(null);
  // C2.3 — origin turn for a reconsider-spawn; opens InvitePeerHost in reconsider mode (Task 10).
  const [reconsiderOriginId, setReconsiderOriginId] = useState<string | null>(null);

  // Tools Panel collapse state, persisted. Auto-opens when an artifact
  // click fires from a bubble (via PersonaChatUIProvider's
  // onRequestPanelOpen). When localStorage has no prior preference,
  // falls back to `defaultPanelOpen` — Trailhead passes true so the
  // map is visible on first paint.
  const [panelOpen, setPanelOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(panelOpenKey);
      if (stored === '1') return true;
      if (stored === '0') return false;
      return defaultPanelOpen;
    } catch {
      return defaultPanelOpen;
    }
  });
  // Panel width (px), user-draggable via the rail. Clamped to
  // [180, 800] on drag; below 60 during drag auto-collapses.
  const PANEL_WIDTH_MIN = 180;
  const PANEL_WIDTH_MAX = 800;
  const PANEL_WIDTH_AUTO_CLOSE = 60;
  const PANEL_WIDTH_DEFAULT = 420;
  const [panelWidthPx, setPanelWidthPx] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(panelWidthKey);
      const n = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(n) && n >= PANEL_WIDTH_MIN && n <= PANEL_WIDTH_MAX) return n;
    } catch {
      // fall through to default
    }
    return PANEL_WIDTH_DEFAULT;
  });
  useEffect(() => {
    try {
      localStorage.setItem(panelOpenKey, panelOpen ? '1' : '0');
    } catch {
      // Local storage unavailable — tolerate + move on.
    }
  }, [panelOpen, panelOpenKey]);
  useEffect(() => {
    try {
      localStorage.setItem(panelWidthKey, String(panelWidthPx));
    } catch {
      // Same tolerance as above.
    }
  }, [panelWidthPx, panelWidthKey]);

  // Drag state for the rail. Ref holds starting positions + a `moved`
  // flag so pointer-up can distinguish a click (toggle) from a drag
  // (commit width). React state only for the `dragging` boolean so the
  // panel can disable its width-transition while the user is actively
  // resizing (otherwise it visibly lags).
  const dragRef = useRef<{ startX: number; startWidth: number; moved: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleRailPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startWidth: panelOpen ? panelWidthPx : 0,
        moved: false,
      };
    },
    [panelOpen, panelWidthPx],
  );

  const handleRailPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const st = dragRef.current;
      if (!st) return;
      // Rail is on the right of the chat column; panel sits to the
      // right of the rail. Dragging left (clientX decreases) should
      // GROW the panel.
      const dx = st.startX - e.clientX;
      if (!st.moved && Math.abs(dx) > 3) {
        st.moved = true;
        setDragging(true);
      }
      if (!st.moved) return;
      const newWidth = st.startWidth + dx;
      if (newWidth < PANEL_WIDTH_AUTO_CLOSE) {
        if (panelOpen) setPanelOpen(false);
      } else {
        if (!panelOpen) setPanelOpen(true);
        const clamped = Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, newWidth));
        setPanelWidthPx(clamped);
      }
    },
    [panelOpen],
  );

  const handleRailPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Not captured — fine.
      }
      const st = dragRef.current;
      dragRef.current = null;
      setDragging(false);
      // No meaningful movement = treat as click toggle.
      if (st && !st.moved) {
        setPanelOpen((v) => !v);
      }
    },
    [],
  );

  useEffect(() => {
    if (isFallback) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (personaDetailsRef.current && !personaDetailsRef.current.contains(event.target as Node)) {
        setShowPersonaDetails(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFallback]);

  return (
    <div className={`w-full h-full flex flex-col ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      {!theater && <LlmDataWarning />}

      {/* Council Trail Task 6.8 — wake-brief banner sits at the surface
          root as a sibling of LlmDataWarning so it stays out of the
          message-stream flex chain (per plan rev 2 C4). Inactive when
          CouncilProvider isn't mounted higher up (Solo Trail surfaces). */}
      <CouncilWakeBriefBannerSlot sessionId={sessionId} />

      <TrailheadLayout
        personaId={persona.id}
        sessionId={sessionId || ''}
        personaName={persona.name}
        personaCardSlot={!isFallback ? (
          <div
            ref={personaDetailsRef}
            className={`rounded-lg shadow-lg border-l-4 px-4 py-3 cursor-pointer transition-all hover:shadow-xl relative ${
              darkMode ? 'bg-gray-800 border-emerald-600' : 'bg-white border-emerald-500 shadow-sm'
            }`}
            onClick={() => setShowPersonaDetails(true)}
          >
            <div className="flex gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <PersonaBadge personaId={persona.id} personaName={persona.name} size="lg" clickable={false} hideName />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-base font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      {persona.name.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {persona.primarySpecialization || persona.shortDescription || 'Conversational AI'}
                    </span>
                    {persona.revision != null && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        rev {persona.revision}
                      </span>
                    )}
                    {session?.created_at && (
                      <span className={`text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {(() => {
                          try {
                            const d = Date.now() - new Date(session.created_at).getTime();
                            const m = Math.floor(d / 60000);
                            if (m < 60) return `${m}m`;
                            const h = Math.floor(m / 60);
                            if (h < 24) return `${h}h`;
                            return `${Math.floor(h / 24)}d`;
                          } catch {
                            return '';
                          }
                        })()}{' '}
                        session
                      </span>
                    )}
                    {session?.message_count != null && (
                      <span
                        className={`flex items-center gap-1 text-[11px] ${
                          darkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}
                      >
                        <FiMessageSquare className="w-3 h-3" /> {session.message_count} msgs
                      </span>
                    )}
                    <SessionCostChip />
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPersonaDetails(!showPersonaDetails);
                  }}
                  className={`px-2 py-1 rounded text-xs ${
                    darkMode ? 'text-emerald-400 hover:bg-emerald-900/20' : 'text-emerald-600 hover:bg-emerald-50'
                  }`}
                >
                  Quick View
                </button>
                <button
                  onClick={onBack}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <FiArrowLeft className="w-3 h-3" /> Back
                </button>
              </div>
            </div>
            <PersonaHoverCard persona={persona} isVisible={showPersonaDetails} />
          </div>
        ) : undefined}
        chatTrailSlot={(
          <>
            {isFallback && (
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                  <div className={`mr-3 p-2 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                    <FiMessageSquare className={`h-6 w-6 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`} />
                  </div>
                  <div>
                    <h1
                      className={`text-2xl font-bold ${
                        darkMode
                          ? 'text-blue-300 border-l-4 border-blue-700 pl-3'
                          : 'text-blue-700 border-l-4 border-blue-500 pl-3'
                      }`}
                    >
                      Chat with {persona.name}
                    </h1>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {persona.shortDescription || 'AI Assistant'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {sessionSkills.map((s: any) => (
                      <SkillBadge key={s.skill_name} name={s.skill_name} />
                    ))}
                    {onShowSkillPicker && (
                      <button
                        onClick={onShowSkillPicker}
                        className={`px-3 py-1.5 rounded-md text-xs flex items-center ${
                          darkMode
                            ? 'bg-gray-700 hover:bg-gray-600 text-white'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                      >
                        + Skills
                      </button>
                    )}
                  </div>
                  <button
                    onClick={onBack}
                    className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                      darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-sm'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700 shadow-sm'
                    }`}
                  >
                    <FiArrowLeft className="mr-1.5" />
                    Back to Chats
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 flex flex-col min-h-0 min-w-0">
              <div
                className={
                  isFallback
                    ? `flex-1 flex flex-col min-h-0 min-w-0 rounded-lg shadow-lg p-0 ${
                        darkMode ? 'bg-gray-800 border-l-4 border-blue-700' : 'bg-white border-l-4 border-blue-500'
                      }`
                    : `flex-1 flex flex-col min-h-0 min-w-0 rounded-lg shadow-lg overflow-hidden ${
                        darkMode ? 'bg-gray-800 border-l-4 border-emerald-700' : 'bg-white border-l-4 border-emerald-500'
                      }`
                }
              >
                <div className="flex flex-1 min-h-0 min-w-0">
                  <ChatDagRuntimeProvider
                    collapsedFanIds={collapsedFanIds}
                    viewportTurnId={viewportTurnId}
                    livePhaseEvents={phaseEvents}
                    wsLastEventMs={wsLastEventMs}
                    inFlightTurnId={inFlightTurnId}
                    tabFocusTurnId={tabFocusTurnId}
                    lastInFlightTurnId={lastInFlightTurnId}
                    inFlightStartedAtMs={inFlightStartedAtMs}
                    setTabFocusTurnId={setTabFocusTurnId}
                    errorTurnId={errorTurnId}
                    allFansCollapsed={allFansCollapsed}
                    showPhaseStacks={showPhaseStacks}
                    collapsedPhaseStackIds={collapsedPhaseStackIds}
                    allPhaseStacksCollapsed={allPhaseStacksCollapsed}
                    expandedArtifactTurnIds={expandedArtifactTurnIds}
                    activeDim={activeDim}
                    setActiveDim={setActiveDim}
                    expandedArtifactIds={expandedArtifactIds}
                    toggleArtifactExpansion={toggleArtifactExpansion}
                    collapseAllArtifacts={collapseAllArtifacts}
                    expandedCardSpineIds={expandedCardSpineIds}
                    toggleCardSpineExpansion={toggleCardSpineExpansion}
                    seedCardSpineExpanded={seedCardSpineExpanded}
                  >
                    <ChatDagDimensionsProvider>
                      <PersonaChatUIProvider
                        handlerRefs={panelHandlerRefs}
                        onRequestPanelOpen={() => setPanelOpen(true)}
                        onToggleFanExpansion={toggleFanExpansion}
                        onFocusBubble={focusBubble}
                        onSetAllFansCollapsed={setAllFansCollapsed}
                        onSetShowPhaseStacks={setShowPhaseStacks}
                        onTogglePhaseStackCollapse={togglePhaseStackCollapse}
                        onSetAllPhaseStacksCollapsed={setAllPhaseStacksCollapsed}
                        onToggleArtifactsExpanded={toggleArtifactsExpanded}
                        onOpenInvitePeer={setInviteFromTurnId}
                        canInvitePeer={inviteEnabled}
                        onOpenInviteToTrail={setInviteTrailId}
                        onOpenFoldBack={setFoldBackCampId}
                        canFoldBack={foldBackEnabled}
                        onOpenReconsider={setReconsiderOriginId}
                        canReconsider={foldBackEnabled}
                        canEditHill={foldBackEnabled}
                        onEditHillTarget={handleEditHill}
                        onForkHillTarget={handleForkHill}
                        onForkReroll={handleForkReroll}
                      >
                        {/* Content column expands to fill when the ChatMap panel
                            is collapsed; the panel lives in a transitioning-width
                            column on the right. The rail between them is always
                            visible and is the primary open/close affordance so
                            users never lose sight of how to get the panel back. */}
                        <div className="flex-1 flex flex-col border-r border-gray-700/40 min-w-0 transition-[flex-basis] duration-200">
                          <TrailObjectiveBanner sessionId={sessionId} />
                          <WorkshopRow personaId={persona.id} persona={persona} sessionId={sessionId} />
                          <div className="flex-1 min-h-0 relative">
                            {crewArriving && (
                              <div
                                data-testid="crew-arriving"
                                className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full
                                           text-xs font-medium pointer-events-none
                                           bg-amber-100 text-amber-800 border border-amber-300
                                           dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/60"
                              >
                                ⛰ the crew is arriving…
                              </div>
                            )}
                            <ConversationMessageStream
                              persona={persona}
                              sessionId={sessionId}
                              isFallback={isFallback}
                              loadingStatus={loadingStatus}
                              phaseEvents={phaseEvents}
                              autoFocus={autoFocus}
                            />
                            {showZeroStateHint && (
                              <div
                                data-testid="chat-zero-state"
                                className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 pointer-events-none"
                              >
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                  Type below to start the trail
                                </p>
                                <span className="text-2xl text-gray-400 dark:text-gray-500" aria-hidden>↓</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Always-visible draggable rail. Click toggles
                            open/close; drag resizes the panel; drag-past-min
                            auto-collapses. Vertical gradient "🔗 ChatMap" label
                            — colorful + eye-catching so the affordance
                            telegraphs "something lives here" without needing
                            a user to know to hunt for it. `cursor-col-resize`
                            hints at draggability. */}
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={panelOpen ? 'Resize or hide ChatMap panel' : 'Resize or show ChatMap panel'}
                          aria-expanded={panelOpen}
                          aria-controls="persona-chatdag-panel"
                          onPointerDown={handleRailPointerDown}
                          onPointerMove={handleRailPointerMove}
                          onPointerUp={handleRailPointerUp}
                          onPointerCancel={handleRailPointerUp}
                          className={`group flex flex-col items-center justify-center gap-3 w-7 shrink-0 select-none cursor-col-resize border-r touch-none ${
                            darkMode
                              ? 'border-gray-700/40 hover:bg-emerald-900/20 active:bg-emerald-900/30'
                              : 'border-gray-200 hover:bg-emerald-50 active:bg-emerald-100'
                          } ${dragging ? (darkMode ? 'bg-emerald-900/30' : 'bg-emerald-100') : ''}`}
                        >
                          <span
                            className={`text-sm font-bold ${
                              darkMode ? 'text-emerald-300' : 'text-emerald-600'
                            } group-hover:scale-125 transition-transform`}
                          >
                            {panelOpen ? '›' : '‹'}
                          </span>
                          <span className="text-base" aria-hidden>
                            🔗
                          </span>
                          <span
                            className="text-[11px] font-bold uppercase tracking-widest bg-gradient-to-b from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent drop-shadow"
                            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                          >
                            ChatMap
                          </span>
                          {/* Tiny grip-dots hint that the rail is grabbable. */}
                          <span
                            className={`flex flex-col gap-0.5 ${
                              darkMode ? 'text-emerald-400/60' : 'text-emerald-500/70'
                            }`}
                            aria-hidden
                          >
                            <span className="block w-0.5 h-0.5 rounded-full bg-current" />
                            <span className="block w-0.5 h-0.5 rounded-full bg-current" />
                            <span className="block w-0.5 h-0.5 rounded-full bg-current" />
                          </span>
                        </div>

                        {/* Panel column — width animates to 0 when collapsed,
                            transition disables during active drag so the width
                            follows the pointer. ChatDagCanvas is the sole
                            resident now (Tools panel removed 2026-04-24); kept
                            always-mounted so the DAG's fit / zoom / camera
                            state survives panel open/close cycles. */}
                        <div
                          id="persona-chatdag-panel"
                          className={`min-w-0 overflow-hidden flex flex-col ${
                            dragging ? '' : 'transition-[width] duration-200 ease-out'
                          }`}
                          style={{ width: panelOpen ? `${panelWidthPx}px` : '0px' }}
                          aria-hidden={!panelOpen}
                        >
                          <div className="flex-1 min-h-0 relative">
                            <ChatDagErrorBoundary>
                              <ChatDagCanvas primaryPersona={persona} sessionId={sessionId} handlerRefs={panelHandlerRefs} greetingPending={greetPending} />
                            </ChatDagErrorBoundary>
                          </div>
                        </div>
                        {/* G3a — Expedition Log drawer, beside the ChatMap panel */}
                        {logOpen && <ExpeditionLog sessionId={sessionId} darkMode={darkMode} />}
                        {inviteEnabled && inviteFromTurnId && (
                          <InvitePeerHost
                            sessionId={sessionId}
                            fromTurnId={inviteFromTurnId}
                            onClose={() => setInviteFromTurnId(null)}
                          />
                        )}
                        {/* Shared spines P2 — trail-join host (camp ⋯ menu). */}
                        {inviteEnabled && inviteTrailId && (
                          <InvitePeerHost
                            sessionId={sessionId}
                            fromTurnId={null}
                            inviteTrailId={inviteTrailId}
                            onClose={() => setInviteTrailId(null)}
                          />
                        )}
                        {foldBackEnabled && foldBackCampId && (
                          <FoldBackDialog
                            sessionId={sessionId}
                            campId={foldBackCampId}
                            onClose={() => setFoldBackCampId(null)}
                          />
                        )}
                        {/* C2.3 — reconsider-spawn reuses InvitePeerHost in reconsider mode. */}
                        {foldBackEnabled && reconsiderOriginId && (
                          <InvitePeerHost
                            sessionId={sessionId}
                            fromTurnId={reconsiderOriginId}
                            reconsider
                            onClose={() => setReconsiderOriginId(null)}
                          />
                        )}
                      </PersonaChatUIProvider>
                    </ChatDagDimensionsProvider>
                  </ChatDagRuntimeProvider>
                </div>
              </div>
            </div>
          </>
        )}
      />
    </div>
  );
};
